import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    CubeGroupsState,
    SourceState,
    FilterState,
    CubeGroup,
    Cube,
    Report,
    LookupState
} from './types';
import { detailTypes } from 'common/enums';

const defaultFilter: FilterState = {
    pageNumber: 1,
    pageSize: 10,
    searchString: '',
    taskId: 0
};

const initialState: CubeGroupsState = {
    masterSource: {
        data: [],
        total: 0,
        filter: { ...defaultFilter }
    },
    detailSource: {
        data: [],
        total: 0,
        filter: { ...defaultFilter }
    },
    reportSource: {
        data: []
    },
    lookups: {
        libraries: [],
        scopes: [],
        task: null,
    },
    hasPendingChanges: false,
    masterSpinner: false,
    detailSpinner: false,
    reportsSpinner: false,
    masterSelected: [],
    detailSelected: [],
    detailLevel: detailTypes.linked,
    templateEditModal: {
        open: false,
        locked: false,
        header: '',
        source: {
            templateId: null,
            templateName: '',
            description: null
        }
    }
};

const cubeGroupsSlice = createSlice({
    name: 'cubeGroups',
    initialState,
    reducers: {
        toggleMasterSpinner(state, action: PayloadAction<boolean>) {
            state.masterSpinner = action.payload;
        },
        toggleDetailSpinner(state, action: PayloadAction<boolean>) {
            state.detailSpinner = action.payload;
        },
        toggleReportsSpinner(state, action: PayloadAction<boolean>) {
            state.reportsSpinner = action.payload;
        },
        setMasterSource(state, action: PayloadAction<SourceState<CubeGroup>>) {
            state.masterSource.data = action.payload.data;
            state.masterSource.total = action.payload.total;
            state.masterSource.filter = {
                ...state.masterSource.filter,
                ...action.payload.filter,
            };
        },
        setDetailSource(state, action: PayloadAction<SourceState<Cube>>) {
            state.detailSource.data = action.payload.data;
            state.detailSource.total = action.payload.total;
            state.detailSource.filter = {
                ...state.detailSource.filter,
                ...action.payload.filter,
            };
        },
        setReportsSource(state, action: PayloadAction<Report[]>) {
            state.reportSource.data = action.payload;
        },
        changeMasterSelection(state, action: PayloadAction<number | null>) {
            state.masterSelected = action.payload != null ? [action.payload] : [];
        },
        changeDetailSelection(state, action: PayloadAction<number | null>) {
            state.detailSelected = action.payload != null ? [action.payload] : [];
        },
        changeMasterChecked(state, action: PayloadAction<number[]>) {
            state.masterSelected = action.payload;
        },
        changeDetailChecked(state, action: PayloadAction<number[]>) {
            state.detailSelected = action.payload;
        },
        changeDetailLevel(state, action: PayloadAction<number>) {
            state.detailLevel = action.payload;
        },
        updateReports(state, action: PayloadAction<Report[]>) {
            state.reportSource.data = action.payload;
        },
        setMasterFilter(state, action: PayloadAction<Partial<FilterState>>) {
            state.masterSource.filter = {
                ...state.masterSource.filter,
                ...action.payload,
            };
        },
        setDetailFilter(state, action: PayloadAction<Partial<FilterState>>) {
            state.detailSource.filter = {
                ...state.detailSource.filter,
                ...action.payload,
            };
        },
        setLookups(state, action: PayloadAction<LookupState>) {
            state.lookups = action.payload;
        },
        setClean(state) {
            state.hasPendingChanges = false;
        },
        setDirty(state) {
            state.hasPendingChanges = true;
        },
        openTemplateEditModal(state, action: PayloadAction<{ header: string, template?: Partial<CubeGroup> }>) {
            state.templateEditModal.open = true;
            state.templateEditModal.header = action.payload.header;
            if (action.payload.template) state.templateEditModal.source = { ...state.templateEditModal.source, ...action.payload.template }
        },
        toggleTemplateEditModalSpinner(state, action: PayloadAction<boolean>) {
            state.templateEditModal.locked = action.payload;
        },
        changeTemplateEditModalField(state, action: PayloadAction<{ [key: string]: string }>) {
            state.templateEditModal.source = { ...state.templateEditModal.source, ...action.payload }
        },
        closeTemplateEditModal(state) {
            state.templateEditModal = { ...initialState.templateEditModal }
        },
    },
});

export const actions = cubeGroupsSlice.actions;
export default cubeGroupsSlice.reducer;
